import React, { useEffect, useMemo, useState } from "react";
import { usePmsTheme } from "../Components/PmsThemeContext";
import {
  createRmsConfigResource,
  deleteRmsConfigResource,
  fetchRmsCompetitorRoomTypesDistinct,
  updateRmsConfigResource,
} from "./rmsApi";
import { createRmsTranslator } from "./rmsPmsI18n";

const safeArray = (value) => (Array.isArray(value) ? value : []);
const normalizeRoomTypeKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
const buildCompetitorKey = (item) =>
  `${Number(item?.hotel_code || 0)}|${normalizeRoomTypeKey(
    item?.room_type_norm || item?.room_type_raw || item?.room_type
  )}`;
const parsePositiveInteger = (value) => {
  const parsed = Number.parseInt(String(value ?? "").trim(), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};
const normalizeOccupancyOptions = (values) =>
  Array.from(
    new Set(
      safeArray(values)
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0)
    )
  ).sort((left, right) => left - right);
const buildOccupancyOptions = (maxValue) => {
  const max = parsePositiveInteger(maxValue) || 6;
  return Array.from({ length: max }, (_item, index) => index + 1);
};
const formatLaneCount = (count, singular, plural, t = null) => {
  const label = count === 1 ? singular : plural;
  return `${count} ${t ? t(label) : label}`;
};
const formatGuestLabel = (value, t = null) => `${value} ${t ? t("pax") : "pax"}`;
const formatGuestRange = (values, t = null) => {
  const options = normalizeOccupancyOptions(values);
  if (!options.length) return t ? t("Pick pax") : "Pick pax";
  if (options.length === 1) return formatGuestLabel(options[0], t);
  return `${options[0]}-${options[options.length - 1]} ${t ? t("pax") : "pax"}`;
};
const formatGuestOptions = (values, t = null) => {
  const options = normalizeOccupancyOptions(values);
  return options.length
    ? options.map((value) => formatGuestLabel(value, t)).join(", ")
    : t
      ? t("No guest options found yet")
      : "No guest options found yet";
};
const getRoomCategoryCapacity = (category) => {
  const direct = Number(category?.max_occupancy);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const adults = Number(category?.max_adults);
  const children = Number(category?.max_children);
  const total =
    (Number.isFinite(adults) ? adults : 0) +
    (Number.isFinite(children) ? children : 0);
  return total > 0 ? total : 1;
};
const formatRoomCategoryCapacity = (category, t = null) => {
  const total = getRoomCategoryCapacity(category);
  const adults = Number(category?.max_adults);
  const children = Number(category?.max_children);
  const parts = [];
  if (Number.isFinite(adults) && adults > 0) {
    parts.push(
      `${adults} ${
        t ? (adults === 1 ? t("adult") : t("adults")) : `adult${adults === 1 ? "" : "s"}`
      }`
    );
  }
  if (Number.isFinite(children) && children > 0) {
    parts.push(
      `${children} ${
        t ? (children === 1 ? t("child") : t("children")) : `child${children === 1 ? "" : "ren"}`
      }`
    );
  }
  return parts.length
    ? `${total} ${t ? t("total") : "total"} - ${parts.join(" + ")}`
    : `${total} ${t ? t("total") : "total"}`;
};
const getStandardTypeDefaultOccupancy = (type) =>
  parsePositiveInteger(type?.default_max_occupancy) || 2;
const buildZoneKey = (kind, standardRoomTypeId = null) =>
  `${kind}:${standardRoomTypeId == null ? "unassigned" : standardRoomTypeId}`;

export default function RmsMarketMappingStudio({
  meta,
  resources,
  loading,
  reloadMappings,
  onError,
}) {
  const { languageId } = usePmsTheme();
  const t = useMemo(() => createRmsTranslator(languageId), [languageId]);
  const [competitorRoomTypes, setCompetitorRoomTypes] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverZone, setDragOverZone] = useState("");
  const [savingKey, setSavingKey] = useState("");
  const [activeComparableGuests, setActiveComparableGuests] = useState("2");
  const [roomCategoryDrafts, setRoomCategoryDrafts] = useState({});
  const [competitorDrafts, setCompetitorDrafts] = useState({});
  const [standardTypeDrafts, setStandardTypeDrafts] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setSourceLoading(true);
        const payload = await fetchRmsCompetitorRoomTypesDistinct();
        if (!cancelled) setCompetitorRoomTypes(safeArray(payload));
      } catch (error) {
        if (!cancelled) {
          onError?.(
              error?.response?.data?.error ||
              error?.message ||
              t("Could not load competitor room types.")
          );
          setCompetitorRoomTypes([]);
        }
      } finally {
        if (!cancelled) setSourceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onError, t]);

  const roomCategories = useMemo(() => safeArray(meta?.room_categories), [meta]);
  const standardRoomTypes = useMemo(
    () =>
      safeArray(meta?.standard_room_types).filter((item) => item?.is_active !== false),
    [meta]
  );
  const competitors = useMemo(() => safeArray(resources?.competitors), [resources]);
  const ownMappings = useMemo(
    () =>
      safeArray(resources?.["room-category-mappings"]).filter((item) => item?.is_active),
    [resources]
  );
  const competitorMappings = useMemo(
    () => safeArray(resources?.["competitor-room-type-mappings"]),
    [resources]
  );

  const roomCategoriesById = useMemo(
    () => new Map(roomCategories.map((item) => [Number(item.id), item])),
    [roomCategories]
  );
  const standardRoomTypesById = useMemo(
    () => new Map(standardRoomTypes.map((item) => [Number(item.id), item])),
    [standardRoomTypes]
  );
  const ownMappingsByRoomCategory = useMemo(
    () => new Map(ownMappings.map((item) => [Number(item.room_category_id), item])),
    [ownMappings]
  );
  const competitorMappingsByKey = useMemo(
    () => new Map(competitorMappings.map((item) => [buildCompetitorKey(item), item])),
    [competitorMappings]
  );

  const competitorNameByHotelCode = useMemo(() => {
    const out = new Map();
    competitors.forEach((item) => {
      const hotelCode = Number(item?.hotel_code);
      const name = String(item?.competitor_name || "").trim();
      if (hotelCode) out.set(hotelCode, name || `Hotel ${hotelCode}`);
    });
    competitorRoomTypes.forEach((item) => {
      const hotelCode = Number(item?.hotel_code);
      const name = String(item?.hotel_name || "").trim();
      if (hotelCode && name && !out.has(hotelCode)) out.set(hotelCode, name);
    });
    return out;
  }, [competitors, competitorRoomTypes]);

  const competitorRoomTypeEntries = useMemo(() => {
    const out = new Map();
    competitorRoomTypes.forEach((item) => {
      const hotelCode = Number(item?.hotel_code || 0);
      const roomTypeRaw = String(item?.room_type || item?.room_type_raw || "").trim();
      if (!hotelCode || !roomTypeRaw) return;
      const key = buildCompetitorKey(item);
      out.set(key, {
        key,
        hotel_code: hotelCode,
        hotel_name:
          String(item?.hotel_name || "").trim() ||
          competitorNameByHotelCode.get(hotelCode) ||
          `Hotel ${hotelCode}`,
        room_type_raw: roomTypeRaw,
        room_type_norm: normalizeRoomTypeKey(item?.room_type_norm || item?.room_type),
        website_code: null,
        room_description: item?.room_description || null,
        room_amenities: item?.room_amenities || null,
        occupancy_options: normalizeOccupancyOptions(item?.occupancy_options),
      });
    });
    competitorMappings.forEach((item) => {
      const key = buildCompetitorKey(item);
      const mappedOcc = parsePositiveInteger(item?.max_occupancy);
      const current = out.get(key);
      if (!current) {
        out.set(key, {
          key,
          hotel_code: Number(item?.hotel_code || 0),
          hotel_name:
            item?.competitor?.competitor_name ||
            competitorNameByHotelCode.get(Number(item?.hotel_code || 0)) ||
            `Hotel ${item?.hotel_code}`,
          room_type_raw: String(item?.room_type_raw || "").trim(),
          room_type_norm: normalizeRoomTypeKey(
            item?.room_type_norm || item?.room_type_raw
          ),
          website_code: item?.website_code ?? null,
          room_description: null,
          room_amenities: null,
          occupancy_options: mappedOcc ? [mappedOcc] : [],
        });
        return;
      }
      if (mappedOcc && !current.occupancy_options.includes(mappedOcc)) {
        current.occupancy_options = [...current.occupancy_options, mappedOcc].sort(
          (left, right) => left - right
        );
      }
      if (!current.website_code && item?.website_code != null) {
        current.website_code = item.website_code;
      }
    });
    return Array.from(out.values()).sort((left, right) => {
      const hotelCompare = String(left.hotel_name || "").localeCompare(
        String(right.hotel_name || "")
      );
      if (hotelCompare !== 0) return hotelCompare;
      return String(left.room_type_raw || "").localeCompare(
        String(right.room_type_raw || "")
      );
    });
  }, [competitorMappings, competitorNameByHotelCode, competitorRoomTypes]);

  const competitorEntriesByKey = useMemo(
    () => new Map(competitorRoomTypeEntries.map((item) => [item.key, item])),
    [competitorRoomTypeEntries]
  );

  useEffect(() => {
    if (!roomCategories.length) return;
    setRoomCategoryDrafts((previous) => {
      const next = {};
      roomCategories.forEach((category) => {
        const existing = ownMappingsByRoomCategory.get(Number(category.id));
        next[category.id] = {
          max_occupancy: String(
            existing?.max_occupancy ??
              previous?.[category.id]?.max_occupancy ??
              getRoomCategoryCapacity(category)
          ),
        };
      });
      return next;
    });
  }, [roomCategories, ownMappingsByRoomCategory]);

  useEffect(() => {
    if (!competitorRoomTypeEntries.length) return;
    setCompetitorDrafts((previous) => {
      const next = {};
      competitorRoomTypeEntries.forEach((entry) => {
        const existing = competitorMappingsByKey.get(entry.key);
        const singleOption = entry.occupancy_options.length === 1 ? entry.occupancy_options[0] : "";
        next[entry.key] = {
          max_occupancy: String(
            existing?.max_occupancy ??
              previous?.[entry.key]?.max_occupancy ??
              singleOption
          ),
        };
      });
      return next;
    });
  }, [competitorMappingsByKey, competitorRoomTypeEntries]);

  useEffect(() => {
    if (!standardRoomTypes.length) return;
    setStandardTypeDrafts((previous) => {
      const next = {};
      standardRoomTypes.forEach((type) => {
        next[type.id] = {
          default_max_occupancy: String(
            type.default_max_occupancy ??
              previous?.[type.id]?.default_max_occupancy ??
              getStandardTypeDefaultOccupancy(type)
          ),
        };
      });
      return next;
    });
  }, [standardRoomTypes]);

  const activeComparableGuestOptions = useMemo(() => {
    const candidates = [
      6,
      ...standardRoomTypes.map((type) => getStandardTypeDefaultOccupancy(type)),
      ...ownMappings.map((item) => Number(item?.max_occupancy || 0)),
      ...competitorMappings.map((item) => Number(item?.max_occupancy || 0)),
      ...roomCategories.map((item) => getRoomCategoryCapacity(item)),
      ...competitorRoomTypeEntries.flatMap((item) =>
        normalizeOccupancyOptions(item.occupancy_options)
      ),
    ].filter((value) => Number.isInteger(value) && value > 0);
    return buildOccupancyOptions(Math.max(...candidates));
  }, [
    competitorMappings,
    competitorRoomTypeEntries,
    ownMappings,
    roomCategories,
    standardRoomTypes,
  ]);

  const currentComparableGuests =
    parsePositiveInteger(activeComparableGuests) ||
    activeComparableGuestOptions[0] ||
    2;

  useEffect(() => {
    if (!activeComparableGuestOptions.length) return;
    if (!activeComparableGuestOptions.includes(currentComparableGuests)) {
      const nextValue = activeComparableGuestOptions.includes(2)
        ? 2
        : activeComparableGuestOptions[0];
      setActiveComparableGuests(String(nextValue));
    }
  }, [activeComparableGuestOptions, currentComparableGuests]);

  useEffect(() => {
    setSelectedItem(null);
  }, [currentComparableGuests]);

  const ownAssignedByStandardType = useMemo(() => {
    const out = new Map(standardRoomTypes.map((type) => [Number(type.id), []]));
    roomCategories.forEach((category) => {
      const mapping = ownMappingsByRoomCategory.get(Number(category.id));
      const standardTypeId = Number(mapping?.standard_room_type_id || 0);
      if (mapping && out.has(standardTypeId)) out.get(standardTypeId).push(category);
    });
    out.forEach((items) =>
      items.sort((left, right) => {
        const leftOcc = Number(
          ownMappingsByRoomCategory.get(Number(left.id))?.max_occupancy || 0
        );
        const rightOcc = Number(
          ownMappingsByRoomCategory.get(Number(right.id))?.max_occupancy || 0
        );
        if (leftOcc !== rightOcc) return leftOcc - rightOcc;
        return String(left.name || "").localeCompare(String(right.name || ""));
      })
    );
    return out;
  }, [ownMappingsByRoomCategory, roomCategories, standardRoomTypes]);

  const competitorAssignedByStandardType = useMemo(() => {
    const out = new Map(standardRoomTypes.map((type) => [Number(type.id), []]));
    competitorRoomTypeEntries.forEach((entry) => {
      const mapping = competitorMappingsByKey.get(entry.key);
      const standardTypeId = Number(mapping?.standard_room_type_id || 0);
      if (mapping && out.has(standardTypeId)) out.get(standardTypeId).push(entry);
    });
    out.forEach((items) =>
      items.sort((left, right) => {
        const leftOcc = Number(competitorMappingsByKey.get(left.key)?.max_occupancy || 0);
        const rightOcc = Number(competitorMappingsByKey.get(right.key)?.max_occupancy || 0);
        if (leftOcc !== rightOcc) return leftOcc - rightOcc;
        const hotelCompare = String(left.hotel_name || "").localeCompare(
          String(right.hotel_name || "")
        );
        if (hotelCompare !== 0) return hotelCompare;
        return String(left.room_type_raw || "").localeCompare(
          String(right.room_type_raw || "")
        );
      })
    );
    return out;
  }, [competitorMappingsByKey, competitorRoomTypeEntries, standardRoomTypes]);

  const ownUnassigned = useMemo(
    () =>
      roomCategories
        .filter((category) => {
          const mapping = ownMappingsByRoomCategory.get(Number(category.id));
          if (mapping?.standard_room_type_id) return false;
          return getRoomCategoryCapacity(category) >= currentComparableGuests;
        })
        .sort((left, right) => String(left.name || "").localeCompare(String(right.name || ""))),
    [currentComparableGuests, ownMappingsByRoomCategory, roomCategories]
  );

  const competitorUnassigned = useMemo(
    () =>
      competitorRoomTypeEntries.filter((entry) => {
        const mapping = competitorMappingsByKey.get(entry.key);
        const options = normalizeOccupancyOptions(entry.occupancy_options);
        const supportsCurrentGuests =
          !options.length || options.includes(currentComparableGuests);
        if (mapping?.standard_room_type_id) return false;
        return supportsCurrentGuests;
      }),
    [competitorMappingsByKey, competitorRoomTypeEntries, currentComparableGuests]
  );

  const selectedOwnCategory = useMemo(
    () =>
      selectedItem?.kind === "own"
        ? roomCategoriesById.get(Number(selectedItem.id)) || null
        : null,
    [roomCategoriesById, selectedItem]
  );
  const selectedOwnMapping = useMemo(
    () =>
      selectedOwnCategory
        ? ownMappingsByRoomCategory.get(Number(selectedOwnCategory.id)) || null
        : null,
    [ownMappingsByRoomCategory, selectedOwnCategory]
  );
  const selectedCompetitorEntry = useMemo(
    () =>
      selectedItem?.kind === "competitor"
        ? competitorEntriesByKey.get(selectedItem.id) || null
        : null,
    [competitorEntriesByKey, selectedItem]
  );
  const selectedCompetitorMapping = useMemo(
    () =>
      selectedCompetitorEntry
        ? competitorMappingsByKey.get(selectedCompetitorEntry.key) || null
        : null,
    [competitorMappingsByKey, selectedCompetitorEntry]
  );
  const selectedMappedStandardType = useMemo(() => {
    const mappedId =
      selectedOwnMapping?.standard_room_type_id ||
      selectedCompetitorMapping?.standard_room_type_id;
    return standardRoomTypesById.get(Number(mappedId || 0)) || null;
  }, [selectedCompetitorMapping, selectedOwnMapping, standardRoomTypesById]);

  const currentMappingSummary = useMemo(
    () => ({
      ownMapped: ownMappings.filter(
        (item) =>
          item?.standard_room_type_id &&
          Number(item?.max_occupancy || 0) === currentComparableGuests
      ).length,
      competitorMapped: competitorMappings.filter(
        (item) =>
          item?.standard_room_type_id &&
          Number(item?.max_occupancy || 0) === currentComparableGuests
      ).length,
    }),
    [competitorMappings, currentComparableGuests, ownMappings]
  );

  const studioLoading = loading || sourceLoading;

  const setRootError = (message) => {
    if (typeof onError === "function") onError(message || "");
  };

  const beginDrag = (event, payload) => {
    setDraggedItem(payload);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", JSON.stringify(payload));
  };

  const endDrag = () => {
    setDraggedItem(null);
    setDragOverZone("");
  };

  const readDragPayload = (event) => {
    try {
      const raw = event.dataTransfer.getData("text/plain");
      return raw ? JSON.parse(raw) : draggedItem;
    } catch (_error) {
      return draggedItem;
    }
  };

  const resolveStandardTypeDefault = (standardRoomTypeId) =>
    parsePositiveInteger(standardTypeDrafts[standardRoomTypeId]?.default_max_occupancy) ||
    getStandardTypeDefaultOccupancy(standardRoomTypesById.get(Number(standardRoomTypeId)));

  const assignOwnCategory = async (
    roomCategoryId,
    standardRoomTypeId,
    forcedMaxOccupancy = null
  ) => {
    const category = roomCategoriesById.get(Number(roomCategoryId));
    if (!category || !standardRoomTypesById.get(Number(standardRoomTypeId))) return;
    const hotelCapacity = getRoomCategoryCapacity(category);
    const parsedMaxOccupancy =
      parsePositiveInteger(forcedMaxOccupancy) ||
      parsePositiveInteger(roomCategoryDrafts[roomCategoryId]?.max_occupancy) ||
      currentComparableGuests ||
      resolveStandardTypeDefault(standardRoomTypeId);
    if (!parsedMaxOccupancy) {
      setRootError(
        t("Comparable guests for {name} must be greater than 0.", { name: category.name })
      );
      return;
    }
    if (parsedMaxOccupancy > hotelCapacity) {
      setRootError(`${category.name} can only be compared up to ${hotelCapacity} pax.`);
      return;
    }
    const existing = ownMappingsByRoomCategory.get(Number(roomCategoryId));
    const payload = {
      room_category_id: Number(roomCategoryId),
      standard_room_type_id: Number(standardRoomTypeId),
      max_occupancy: parsedMaxOccupancy,
      notes: existing?.notes || "",
      is_active: true,
    };
    try {
      setRootError("");
      setSavingKey(`own:${roomCategoryId}`);
      if (existing?.id) {
        await updateRmsConfigResource("room-category-mappings", existing.id, payload);
      } else {
        await createRmsConfigResource("room-category-mappings", payload);
      }
      await reloadMappings();
      setSelectedItem(null);
    } catch (error) {
      setRootError(
        error?.response?.data?.error ||
          error?.message ||
          "Could not save room category mapping."
      );
    } finally {
      setSavingKey("");
      setDragOverZone("");
    }
  };

  const clearOwnCategory = async (roomCategoryId) => {
    const existing = ownMappingsByRoomCategory.get(Number(roomCategoryId));
    const category = roomCategoriesById.get(Number(roomCategoryId));
    if (!existing?.id || !category) return;
    try {
      setRootError("");
      setSavingKey(`own:${roomCategoryId}`);
      await deleteRmsConfigResource("room-category-mappings", existing.id);
      await reloadMappings();
      setSelectedItem(null);
    } catch (error) {
      setRootError(
        error?.response?.data?.error ||
          error?.message ||
          `Could not remove mapping for ${category.name}.`
      );
    } finally {
      setSavingKey("");
      setDragOverZone("");
    }
  };

  const assignCompetitorRoomType = async (
    entryKey,
    standardRoomTypeId,
    forcedMaxOccupancy = null
  ) => {
    const entry = competitorEntriesByKey.get(entryKey);
    if (!entry || !standardRoomTypesById.get(Number(standardRoomTypeId))) return;
    const parsedMaxOccupancy =
      parsePositiveInteger(forcedMaxOccupancy) ||
      parsePositiveInteger(competitorDrafts[entryKey]?.max_occupancy) ||
      currentComparableGuests ||
      resolveStandardTypeDefault(standardRoomTypeId);
    const options = normalizeOccupancyOptions(entry.occupancy_options);
    if (!parsedMaxOccupancy) {
      setRootError(
        t("Comparable guests for {name} must be greater than 0.", {
          name: entry.room_type_raw,
        })
      );
      return;
    }
    if (options.length && !options.includes(parsedMaxOccupancy)) {
      setRootError(
        t("{name} only has market rows for {options}.", {
          name: entry.room_type_raw,
          options: formatGuestOptions(options, t),
        })
      );
      return;
    }
    const existing = competitorMappingsByKey.get(entryKey);
    const payload = {
      hotel_code: entry.hotel_code || null,
      website_code: entry.website_code ?? null,
      room_type_raw: entry.room_type_raw,
      room_type_norm: entry.room_type_norm || normalizeRoomTypeKey(entry.room_type_raw),
      standard_room_type_id: Number(standardRoomTypeId),
      max_occupancy: parsedMaxOccupancy,
      notes: existing?.notes || "",
    };
    try {
      setRootError("");
      setSavingKey(`competitor:${entryKey}`);
      if (existing?.id) {
        await updateRmsConfigResource("competitor-room-type-mappings", existing.id, payload);
      } else {
        await createRmsConfigResource("competitor-room-type-mappings", payload);
      }
      await reloadMappings();
      setSelectedItem(null);
    } catch (error) {
      setRootError(
        error?.response?.data?.error ||
          error?.message ||
          "Could not save competitor room type mapping."
      );
    } finally {
      setSavingKey("");
      setDragOverZone("");
    }
  };

  const clearCompetitorRoomType = async (entryKey) => {
    const entry = competitorEntriesByKey.get(entryKey);
    const existing = competitorMappingsByKey.get(entryKey);
    if (!existing?.id || !entry) return;
    try {
      setRootError("");
      setSavingKey(`competitor:${entryKey}`);
      await deleteRmsConfigResource("competitor-room-type-mappings", existing.id);
      await reloadMappings();
      setSelectedItem(null);
    } catch (error) {
      setRootError(
        error?.response?.data?.error ||
          error?.message ||
          `Could not remove mapping for ${entry.room_type_raw}.`
      );
    } finally {
      setSavingKey("");
      setDragOverZone("");
    }
  };

  const saveSelectedComparableGuests = async () => {
    if (selectedOwnCategory && selectedOwnMapping?.standard_room_type_id) {
      return assignOwnCategory(
        selectedOwnCategory.id,
        Number(selectedOwnMapping.standard_room_type_id)
      );
    }
    if (selectedCompetitorEntry && selectedCompetitorMapping?.standard_room_type_id) {
      return assignCompetitorRoomType(
        selectedCompetitorEntry.key,
        Number(selectedCompetitorMapping.standard_room_type_id)
      );
    }
  };

  const moveSelectedItemToUnassigned = async (kind) => {
    if (!selectedItem || selectedItem.kind !== kind) return;
    if (kind === "own") return clearOwnCategory(selectedItem.id);
    return clearCompetitorRoomType(selectedItem.id);
  };

  const moveSelectedItemToStandard = async (standardRoomTypeId) => {
    if (!selectedItem) return;
    if (selectedItem.kind === "own") {
      return assignOwnCategory(selectedItem.id, standardRoomTypeId, currentComparableGuests);
    }
    return assignCompetitorRoomType(
      selectedItem.id,
      standardRoomTypeId,
      currentComparableGuests
    );
  };

  const handleDropOnLane = async (event, kind, standardRoomTypeId = null) => {
    event.preventDefault();
    const payload = readDragPayload(event);
    setDragOverZone("");
    if (!payload || payload.kind !== kind) return;
    if (kind === "own") {
      if (standardRoomTypeId == null) await clearOwnCategory(payload.id);
      else await assignOwnCategory(payload.id, standardRoomTypeId, currentComparableGuests);
    } else if (standardRoomTypeId == null) {
      await clearCompetitorRoomType(payload.id);
    } else {
      await assignCompetitorRoomType(
        payload.id,
        standardRoomTypeId,
        currentComparableGuests
      );
    }
    setDraggedItem(null);
  };

  const renderOwnCategoryChip = (category) => {
    const categoryId = Number(category.id);
    const mapping = ownMappingsByRoomCategory.get(categoryId);
    const compareGuests =
      parsePositiveInteger(mapping?.max_occupancy) || currentComparableGuests;
    const isSelected = selectedItem?.kind === "own" && Number(selectedItem.id) === categoryId;
    const isSaving = savingKey === `own:${categoryId}`;
    const capacityLabel = t("Hotel capacity: {capacity}", {
      capacity: formatRoomCategoryCapacity(category, t),
    });
    return (
      <div
        key={`own-chip-${categoryId}`}
        role="button"
        tabIndex={0}
        draggable
        aria-label={`${category.name}. ${capacityLabel}`}
        title={`${category.name} · ${capacityLabel}`}
        className={`rmo-mapChip rmo-mapChip--own${isSelected ? " isSelected" : ""}${isSaving ? " isSaving" : ""}`}
        onClick={(event) => {
          event.stopPropagation();
          setSelectedItem((current) =>
            current?.kind === "own" && Number(current.id) === categoryId
              ? null
              : { kind: "own", id: categoryId }
          );
        }}
        onDragStart={(event) => beginDrag(event, { kind: "own", id: categoryId })}
        onDragEnd={endDrag}
      >
        <div className="rmo-mapChipHead">
          <div className="rmo-mapChipTitle">{category.name}</div>
          <span className="rmo-mapChipBadge">{formatGuestLabel(compareGuests, t)}</span>
        </div>
        <div className="rmo-mapChipMeta">{capacityLabel}</div>
      </div>
    );
  };

  const renderCompetitorChip = (entry) => {
    const mapping = competitorMappingsByKey.get(entry.key);
    const compareGuests =
      parsePositiveInteger(mapping?.max_occupancy) || currentComparableGuests;
    const badgeLabel = compareGuests
      ? `${compareGuests} pax`
      : formatGuestRange(entry.occupancy_options, t);
    const isSelected = selectedItem?.kind === "competitor" && selectedItem.id === entry.key;
    const isSaving = savingKey === `competitor:${entry.key}`;
    const competitorMetaLabel = entry.occupancy_options.length
      ? `${entry.hotel_name} - ${t("Market options: {range}", {
          range: formatGuestRange(entry.occupancy_options, t),
        })}`
      : entry.hotel_name;
    return (
      <div
        key={`competitor-chip-${entry.key}`}
        role="button"
        tabIndex={0}
        draggable
        aria-label={`${entry.room_type_raw}. ${competitorMetaLabel}`}
        title={`${entry.room_type_raw} · ${competitorMetaLabel}`}
        className={`rmo-mapChip rmo-mapChip--competitor${isSelected ? " isSelected" : ""}${isSaving ? " isSaving" : ""}`}
        onClick={(event) => {
          event.stopPropagation();
          setSelectedItem((current) =>
            current?.kind === "competitor" && current.id === entry.key
              ? null
              : { kind: "competitor", id: entry.key }
          );
        }}
        onDragStart={(event) => beginDrag(event, { kind: "competitor", id: entry.key })}
        onDragEnd={endDrag}
      >
        <div className="rmo-mapChipHead">
          <div className="rmo-mapChipTitle">{entry.room_type_raw}</div>
          <span className="rmo-mapChipBadge rmo-mapChipBadge--competitor">{badgeLabel}</span>
        </div>
        <div className="rmo-mapChipMeta">{competitorMetaLabel}</div>
      </div>
    );
  };

  return (
    <div className="rmo-surface rmo-mappingSurface">
      <div className="rmo-head">
        <div>
          <h3 className="rmo-headTitle">{t("Market Mapping Board")}</h3>
          <div className="rmo-headSubtitle">
            {t(
              "Match your room categories and competitor room types to the shared standard market types."
            )}
          </div>
        </div>
      </div>

      <div className="rmo-contentPad">
        <div className="rmo-selectionBanner rmo-selectionBanner--mapping">
          <div className="rmo-mappingHint">
            <strong>{t("{count} pax view", { count: currentComparableGuests })}</strong>
            <span>
              {selectedItem
                ? t("Choose a standard row for the selected room, or drag it there.")
                : t("Drag an unassigned room into its comparable standard row.")}
            </span>
          </div>
          <div className="rmo-mappingToolbarActions">
            <div className="rmo-mappingMetric">
              <span>{t("Own mapped")}</span>
              <strong>{currentMappingSummary.ownMapped}</strong>
            </div>
            <div className="rmo-mappingMetric">
              <span>{t("Competitor mapped")}</span>
              <strong>{currentMappingSummary.competitorMapped}</strong>
            </div>
            <label className="rmo-standardColumnControl rmo-standardColumnControl--compact">
              <span>{t("Showing now")}</span>
              <select
                className="rmo-select rmo-standardColumnSelect"
                value={String(currentComparableGuests)}
                onChange={(event) => setActiveComparableGuests(event.target.value)}
              >
                {activeComparableGuestOptions.map((value) => (
                  <option key={`std-global-occ-${value}`} value={value}>
                    {value} {t("pax")}
                  </option>
                ))}
              </select>
            </label>
            {selectedItem ? (
              <>
                {selectedMappedStandardType ? (
                  <button
                    type="button"
                    className="rmo-btn"
                    onClick={() => moveSelectedItemToUnassigned(selectedItem.kind)}
                  >
                    {t("Send to unassigned")}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="rmo-btn"
                  onClick={() => setSelectedItem(null)}
                >
                  {t("Clear selection")}
                </button>
              </>
            ) : null}
          </div>
        </div>

        {studioLoading ? (
          <div className="rmo-empty">{t("Loading mapping studio...")}</div>
        ) : !standardRoomTypes.length ? (
          <div className="rmo-empty">{t("No standard market types are available yet.")}</div>
        ) : (
          <div
            className={`rmo-mappingStudio rmo-mappingStudio--compact${
              selectedItem ? " hasSelection" : ""
            }`}
          >
            <div className="rmo-mapTriad">
              <div
                className={`rmo-mapRail rmo-mapRail--own${dragOverZone === buildZoneKey("own") ? " isActive" : ""}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragOverZone(buildZoneKey("own"));
                }}
                onDragLeave={() => {
                  if (dragOverZone === buildZoneKey("own")) setDragOverZone("");
                }}
                onDrop={(event) => handleDropOnLane(event, "own")}
              >
                <div className="rmo-mapRailHeader">
                  <div className="rmo-mapRailEyebrow">{t("Your hotel")}</div>
                  <div className="rmo-mapRailTitle">{t("My room categories")}</div>
                  <div className="rmo-mapRailCounter">
                    {formatLaneCount(
                      ownUnassigned.length,
                      "category available",
                      "categories available",
                      t
                    )}
                  </div>
                </div>
                <div className="rmo-mapRailList">
                  {ownUnassigned.length ? (
                    ownUnassigned.map(renderOwnCategoryChip)
                  ) : (
                    <div className="rmo-mapLaneEmpty">
                      {t(
                        "No room category is waiting to be classified for {count} pax.",
                        { count: currentComparableGuests }
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="rmo-standardColumn">
                <div className="rmo-standardColumnHeader">
                  <div className="rmo-standardColumnHeaderTop">
                    <div>
                      <div className="rmo-mapRailEyebrow">{t("Shared market types")}</div>
                      <div className="rmo-mapRailTitle">{t("Standard room types")}</div>
                    </div>
                  </div>
                </div>
                <div className="rmo-standardColumnList">
                  {standardRoomTypes.map((standardType) => {
                    const ownAssigned = (
                      ownAssignedByStandardType.get(Number(standardType.id)) || []
                    ).filter(
                      (item) =>
                        Number(
                          ownMappingsByRoomCategory.get(Number(item.id))?.max_occupancy || 0
                        ) === currentComparableGuests
                    );
                    const competitorAssigned =
                      (
                        competitorAssignedByStandardType.get(Number(standardType.id)) || []
                      ).filter(
                        (item) =>
                          Number(competitorMappingsByKey.get(item.key)?.max_occupancy || 0) ===
                          currentComparableGuests
                      );
                    const isActiveTarget =
                      dragOverZone === `standard:${standardType.id}` ||
                      (selectedMappedStandardType?.id === standardType.id && !!selectedItem);

                    return (
                      <div
                        key={standardType.id}
                        className={`rmo-standardCard${isActiveTarget ? " isActive" : ""}`}
                        onDragOver={(event) => {
                          event.preventDefault();
                          setDragOverZone(`standard:${standardType.id}`);
                        }}
                        onDragLeave={() => {
                          if (dragOverZone === `standard:${standardType.id}`) setDragOverZone("");
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          const payload = readDragPayload(event);
                          setDragOverZone("");
                          if (!payload) return;
                          if (payload.kind === "own") {
                            assignOwnCategory(
                              payload.id,
                              Number(standardType.id),
                              currentComparableGuests
                            );
                          }
                          if (payload.kind === "competitor") {
                            assignCompetitorRoomType(
                              payload.id,
                              Number(standardType.id),
                              currentComparableGuests
                            );
                          }
                          setDraggedItem(null);
                        }}
                        onClick={() => moveSelectedItemToStandard(Number(standardType.id))}
                      >
                        <div className="rmo-standardCardHeader">
                          <div>
                            <div className="rmo-standardCardCode">{standardType.code}</div>
                            <div className="rmo-standardCardName">{standardType.name_en}</div>
                            {standardType.description_en ? (
                              <div
                                className="rmo-standardCardText"
                                title={standardType.description_en}
                              >
                                {standardType.description_en}
                              </div>
                            ) : null}
                          </div>
                          <div className="rmo-standardCardTools">
                            <div className="rmo-standardCardDefault">
                              {currentComparableGuests} {t("pax")}
                            </div>
                            <div className="rmo-standardCardTotal">
                              <strong>{ownAssigned.length + competitorAssigned.length}</strong>
                              <span>{t("mapped")}</span>
                            </div>
                          </div>
                        </div>
                        {ownAssigned.length + competitorAssigned.length === 0 ? (
                          <div className="rmo-standardCardEmpty">
                            {t("Drop a room here to start mapping.")}
                          </div>
                        ) : (
                          <div className="rmo-standardCardGroups">
                            <div className="rmo-standardGroup rmo-standardGroup--own">
                              <div className="rmo-standardGroupHead">
                                <span>{t("Own")}</span>
                                <span>{ownAssigned.length}</span>
                              </div>
                              <div className="rmo-standardGroupItems">
                                {ownAssigned.length ? (
                                  ownAssigned.map(renderOwnCategoryChip)
                                ) : (
                                  <div className="rmo-standardGroupEmpty">
                                    {t("No category assigned")}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="rmo-standardGroup rmo-standardGroup--competitor">
                              <div className="rmo-standardGroupHead">
                                <span>{t("Competition")}</span>
                                <span>{competitorAssigned.length}</span>
                              </div>
                              <div className="rmo-standardGroupItems">
                                {competitorAssigned.length ? (
                                  competitorAssigned.map(renderCompetitorChip)
                                ) : (
                                  <div className="rmo-standardGroupEmpty">
                                    {t("No competitor type assigned")}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div
                className={`rmo-mapRail rmo-mapRail--competitor${dragOverZone === buildZoneKey("competitor") ? " isActive" : ""}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragOverZone(buildZoneKey("competitor"));
                }}
                onDragLeave={() => {
                  if (dragOverZone === buildZoneKey("competitor")) setDragOverZone("");
                }}
                onDrop={(event) => handleDropOnLane(event, "competitor")}
              >
                <div className="rmo-mapRailHeader">
                  <div className="rmo-mapRailEyebrow">{t("Competition")}</div>
                  <div className="rmo-mapRailTitle">{t("Competitor room types")}</div>
                  <div className="rmo-mapRailCounter">
                    {formatLaneCount(
                      competitorUnassigned.length,
                      "type available",
                      "types available",
                      t
                    )}
                  </div>
                </div>
                <div className="rmo-mapRailList">
                  {competitorUnassigned.length ? (
                    competitorUnassigned.map(renderCompetitorChip)
                  ) : (
                    <div className="rmo-mapLaneEmpty">
                      {t(
                        "No competitor room type is waiting to be classified for {count} pax.",
                        { count: currentComparableGuests }
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {selectedItem ? (
              <aside className="rmo-mapDetail" aria-label={t("Selected room details")}>
                {selectedOwnCategory ? (
                  <div className="rmo-mapDetailBody">
                    <div className="rmo-mapDetailType rmo-mapDetailType--own">
                      {t("Own room category")}
                    </div>
                    <div className="rmo-mapDetailTitle">{selectedOwnCategory.name}</div>
                    <div className="rmo-mapDetailMeta">
                      {formatRoomCategoryCapacity(selectedOwnCategory, t)}
                    </div>
                    <div className="rmo-mapDetailGrid">
                      <div className="rmo-mapDetailField">
                        <span>{t("Current standard type")}</span>
                        <strong>
                          {selectedMappedStandardType
                            ? `${selectedMappedStandardType.code} - ${selectedMappedStandardType.name_en}`
                            : t("Not assigned yet")}
                        </strong>
                      </div>
                      <div className="rmo-mapDetailField">
                        <label htmlFor={`detail-occ-${selectedOwnCategory.id}`}>
                          {t("Comparable guests")}
                        </label>
                        <select
                          id={`detail-occ-${selectedOwnCategory.id}`}
                          className="rmo-mapDetailInput"
                          value={
                            roomCategoryDrafts[selectedOwnCategory.id]?.max_occupancy ??
                            String(getRoomCategoryCapacity(selectedOwnCategory))
                          }
                          onChange={(event) =>
                            setRoomCategoryDrafts((previous) => ({
                              ...previous,
                              [selectedOwnCategory.id]: {
                                ...(previous[selectedOwnCategory.id] || {}),
                                max_occupancy: event.target.value,
                              },
                            }))
                          }
                        >
                          {buildOccupancyOptions(
                            getRoomCategoryCapacity(selectedOwnCategory)
                          ).map((value) => (
                            <option
                              key={`own-occ-${selectedOwnCategory.id}-${value}`}
                              value={value}
                            >
                              {value} {t("pax")}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="rmo-mapDetailField">
                        <span>{t("Standard default")}</span>
                        <strong>
                          {selectedMappedStandardType
                            ? `${resolveStandardTypeDefault(selectedMappedStandardType.id)} ${t("pax")}`
                            : t("Pick a standard type first")}
                        </strong>
                      </div>
                    </div>
                    <div className="rmo-mapDetailText">
                      {t(
                        "This category will only compare against market rows that share the same standard type and the same comparable guest count."
                      )}
                    </div>
                    <div className="rmo-mapDetailActions">
                      {selectedOwnMapping?.standard_room_type_id ? (
                        <button
                          type="button"
                          className="rmo-btn rmo-btn--accent"
                          onClick={saveSelectedComparableGuests}
                        >
                          {t("Save guests")}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="rmo-btn"
                        onClick={() => moveSelectedItemToUnassigned("own")}
                      >
                        {t("Send to unassigned")}
                      </button>
                    </div>
                  </div>
                ) : selectedCompetitorEntry ? (
                  <div className="rmo-mapDetailBody">
                    <div className="rmo-mapDetailType rmo-mapDetailType--competitor">
                      {t("Competitor room type")}
                    </div>
                    <div className="rmo-mapDetailTitle">
                      {selectedCompetitorEntry.room_type_raw}
                    </div>
                    <div className="rmo-mapDetailMeta">
                      {selectedCompetitorEntry.hotel_name}
                    </div>
                    <div className="rmo-mapDetailGrid">
                      <div className="rmo-mapDetailField">
                        <span>{t("Current standard type")}</span>
                        <strong>
                          {selectedMappedStandardType
                            ? `${selectedMappedStandardType.code} - ${selectedMappedStandardType.name_en}`
                            : t("Not assigned yet")}
                        </strong>
                      </div>
                      <div className="rmo-mapDetailField">
                        <label htmlFor={`detail-comp-occ-${selectedCompetitorEntry.key}`}>
                          {t("Comparable guests")}
                        </label>
                        <select
                          id={`detail-comp-occ-${selectedCompetitorEntry.key}`}
                          className="rmo-mapDetailInput"
                          value={
                            competitorDrafts[selectedCompetitorEntry.key]?.max_occupancy ??
                            String(
                              selectedCompetitorEntry.occupancy_options.length === 1
                                ? selectedCompetitorEntry.occupancy_options[0]
                                : resolveStandardTypeDefault(selectedMappedStandardType?.id)
                            )
                          }
                          onChange={(event) =>
                            setCompetitorDrafts((previous) => ({
                              ...previous,
                              [selectedCompetitorEntry.key]: {
                                ...(previous[selectedCompetitorEntry.key] || {}),
                                max_occupancy: event.target.value,
                              },
                            }))
                          }
                        >
                          {(selectedCompetitorEntry.occupancy_options.length
                            ? selectedCompetitorEntry.occupancy_options
                            : buildOccupancyOptions(
                                Math.max(
                                  resolveStandardTypeDefault(selectedMappedStandardType?.id),
                                  4
                                )
                              )
                          ).map((value) => (
                            <option
                              key={`comp-occ-${selectedCompetitorEntry.key}-${value}`}
                              value={value}
                            >
                              {value} {t("pax")}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="rmo-mapDetailField">
                        <span>{t("Market options found")}</span>
                        <strong>
                          {formatGuestOptions(selectedCompetitorEntry.occupancy_options, t)}
                        </strong>
                      </div>
                      <div className="rmo-mapDetailField">
                        <span>{t("Normalized name")}</span>
                        <strong>{selectedCompetitorEntry.room_type_norm || "-"}</strong>
                      </div>
                    </div>
                    <div
                      className="rmo-mapDetailText"
                      title={selectedCompetitorEntry.room_description || undefined}
                    >
                      {selectedCompetitorEntry.room_description ||
                        t(
                          "This competitor type will only count for market comparison when both the standard type and the comparable guest count match your own category."
                        )}
                    </div>
                    <div className="rmo-mapDetailActions">
                      {selectedCompetitorMapping?.standard_room_type_id ? (
                        <button
                          type="button"
                          className="rmo-btn rmo-btn--accent"
                          onClick={saveSelectedComparableGuests}
                        >
                          {t("Save guests")}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="rmo-btn"
                        onClick={() => moveSelectedItemToUnassigned("competitor")}
                      >
                        {t("Send to unassigned")}
                      </button>
                    </div>
                  </div>
                ) : null}
              </aside>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
